import {invalidateOnSuccess, metadaApi} from '@dms/api/base';
import {
  CreateServiceVehicleApiRes,
  CreateServiceVehicleApiArg,
  LastActiveServiceCaseApiRes,
  LastActiveServiceCaseApiArg,
  GetServiceVehicleApiRes,
  GetServiceVehicleApiArg,
  CustomerLastActiveServiceCaseApiRes,
  CustomerLastActiveServiceCaseApiArg,
  PatchServiceVehicleApiRes,
  PatchServiceVehicleApiArg,
  GetServiceVehicleV2ApiRes,
  GetServiceVehicleV2ApiArg,
} from '@dms/api/shared';

import {
  GetServiceVehicleAdditionalInformationApiArg,
  GetServiceVehicleAdditionalInformationApiRes,
  PatchServiceVehicleAdditionalInformationApiArg,
  PatchServiceVehicleAdditionalInformationApiRes,
} from './types';

const metadaServiceVehicleApi = metadaApi.injectEndpoints({
  endpoints: (build) => ({
    createServiceVehicle: build.mutation<CreateServiceVehicleApiRes, CreateServiceVehicleApiArg>({
      query: (body) => ({
        url: `/service/v1/service-vehicle`,
        method: 'POST',
        body,
      }),
    }),
    getServiceVehicle: build.query<GetServiceVehicleApiRes, GetServiceVehicleApiArg>({
      query: (params) => ({
        url: `/service/v1/service-vehicle`,
        params,
      }),
    }),
    getServiceVehicleV2: build.query<GetServiceVehicleV2ApiRes, GetServiceVehicleV2ApiArg>({
      query: (params) => ({
        url: `/service/v2/service-vehicle`,
        params,
      }),
      providesTags: (result) => [
        {
          type: 'serviceVehicle',
          id: result?.serviceVehicleId,
        },
      ],
    }),
    getLastActiveServiceCase: build.query<LastActiveServiceCaseApiRes, LastActiveServiceCaseApiArg>(
      {
        query: ({vehicleId}) => ({
          url: `/service/v1/service-case/by-vehicle/${vehicleId}/last-active`,
        }),
      }
    ),
    getCustomerLastActiveServiceCase: build.query<
      CustomerLastActiveServiceCaseApiRes,
      CustomerLastActiveServiceCaseApiArg
    >({
      query: ({customerId}) => ({
        url: `/service/v1/service-case/by-customer/${customerId}/last-active`,
      }),
    }),
    patchServiceVehicle: build.mutation<PatchServiceVehicleApiRes, PatchServiceVehicleApiArg>({
      query: (queryArg) => ({
        url: `/service/v1/service-vehicle/${queryArg.serviceVehicleId}`,
        method: 'PATCH',
        body: queryArg.patchServiceVehicleRequestBody,
      }),
      invalidatesTags: (_result, _error, queryArg) => [
        {
          type: 'serviceVehicle',
          id: queryArg.serviceVehicleId,
        },
      ],
    }),
    getServiceVehicleAdditionalInformation: build.query<
      GetServiceVehicleAdditionalInformationApiRes,
      GetServiceVehicleAdditionalInformationApiArg
    >({
      query: (queryArg) => ({
        url: `/service/v1/service-vehicle/${queryArg.serviceVehicleId}/additional-information`,
      }),
      providesTags: (_result, _error, queryArg) => [
        {
          type: 'serviceVehicleAdditionalInformation',
          id: queryArg.serviceVehicleId,
        },
      ],
    }),
    patchServiceVehicleAdditionalInformation: build.mutation<
      PatchServiceVehicleAdditionalInformationApiRes,
      PatchServiceVehicleAdditionalInformationApiArg
    >({
      query: (queryArg) => ({
        url: `/service/v1/service-vehicle/${queryArg.serviceVehicleId}/additional-information/${queryArg.additionalInformationId}`,
        method: 'PATCH',
        body: queryArg.patchAdditionalInformationRequestBody,
      }),
      invalidatesTags: invalidateOnSuccess(
        (_result, queryArg: PatchServiceVehicleAdditionalInformationApiArg) => [
          {
            type: 'serviceVehicleAdditionalInformation',
            id: queryArg.serviceVehicleId,
          },
        ]
      ),
    }),
  }),
});

export const {
  useCreateServiceVehicleMutation,
  useGetLastActiveServiceCaseQuery,
  useGetCustomerLastActiveServiceCaseQuery,
  useGetServiceVehicleQuery,
  useGetServiceVehicleV2Query,
  useLazyGetServiceVehicleQuery,
  usePatchServiceVehicleMutation,
  useGetServiceVehicleAdditionalInformationQuery,
  useLazyGetServiceVehicleAdditionalInformationQuery,
  usePatchServiceVehicleAdditionalInformationMutation,
} = metadaServiceVehicleApi;
